import React, { useEffect, useState } from 'react';
import { Box, VStack, HStack, Text, Heading, Button, Input, FormControl, Alert, AlertIcon, Spinner, Badge, Icon, Image, Link } from '@chakra-ui/react';
import { FaIdCard, FaCertificate, FaCheckCircle, FaUpload, FaTrash, FaFilePdf, FaExternalLinkAlt } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

const ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp,application/pdf';
const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const BUCKET = 'phleb-credentials';

function sanitizeName(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function isImagePath(path) {
  if (!path) return false;
  return /\.(png|jpe?g|webp|gif)$/i.test(path);
}

function isPdfPath(path) {
  return !!path && /\.pdf$/i.test(path);
}

async function uploadCredential({ phlebId, kind, file }) {
  if (file.size > MAX_BYTES) throw new Error('File is too large (max 10 MB).');
  const path = `${phlebId}/${kind}-${Date.now()}-${sanitizeName(file.name)}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { contentType: file.type, upsert: true });
  if (error) throw error;
  return { path, filename: file.name };
}

async function removeCredential(path) {
  if (!path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) throw error;
}

function FilePreview({ path, filename }) {
  const [signedUrl, setSignedUrl] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!path) { setSignedUrl(null); return; }
    (async () => {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(path, 60 * 30); // 30-minute preview link
      if (cancelled) return;
      if (error) setErr(error.message);
      else setSignedUrl(data?.signedUrl || null);
    })();
    return () => { cancelled = true; };
  }, [path]);

  if (err) {
    return <Text fontSize="xs" color="red.500">Couldn't load preview: {err}</Text>;
  }
  if (!signedUrl) {
    return <Box h="120px" display="flex" alignItems="center" justifyContent="center"><Spinner size="sm" /></Box>;
  }

  if (isImagePath(path)) {
    return (
      <Box mt={2} borderWidth="1px" borderRadius="md" overflow="hidden" bg="gray.50">
        <Image src={signedUrl} alt={filename || 'preview'} maxH="220px" mx="auto" objectFit="contain" />
      </Box>
    );
  }
  if (isPdfPath(path)) {
    return (
      <HStack mt={2} p={3} bg="gray.50" borderWidth="1px" borderRadius="md" spacing={3}>
        <Icon as={FaFilePdf} color="red.500" boxSize={5} />
        <Box flex={1}>
          <Text fontSize="sm" fontWeight="600" noOfLines={1}>{filename || 'document.pdf'}</Text>
          <Link href={signedUrl} isExternal fontSize="xs" color="blue.500">
            Open PDF <Icon as={FaExternalLinkAlt} boxSize={2.5} mb={0.5} />
          </Link>
        </Box>
      </HStack>
    );
  }
  return (
    <Link href={signedUrl} isExternal fontSize="xs" color="blue.500" mt={2} display="inline-block">
      View file <Icon as={FaExternalLinkAlt} boxSize={2.5} mb={0.5} />
    </Link>
  );
}

function CredentialCard({ label, description, icon, value, onUpload, onDelete, kind, phlebId }) {
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      // If replacing, remove the old file first so we don't leak storage.
      if (value?.path) {
        try { await removeCredential(value.path); } catch (_) { /* non-fatal */ }
      }
      const result = await uploadCredential({ phlebId, kind, file });
      onUpload(result);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const onDeleteClick = async () => {
    if (!value?.path) return;
    if (!window.confirm(`Delete the uploaded ${label.toLowerCase()}?`)) return;
    setErr(null);
    setDeleting(true);
    try {
      await removeCredential(value.path);
      onDelete();
    } catch (e) {
      setErr(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const uploaded = !!value?.path;

  return (
    <Box borderWidth="1px" borderColor={uploaded ? 'green.200' : 'gray.200'} bg={uploaded ? 'green.50' : 'white'} borderRadius="lg" p={4}>
      <HStack align="start" spacing={3}>
        <Box bg={uploaded ? 'green.100' : 'blue.50'} color={uploaded ? 'green.600' : 'blue.600'} borderRadius="md" boxSize="40px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
          <Icon as={icon} boxSize={4} />
        </Box>
        <Box flex={1}>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="sm" fontWeight="700" color="gray.800">{label}</Text>
            {uploaded && (
              <Badge colorScheme="green" fontSize="2xs" borderRadius="full">
                <HStack spacing={1}><Icon as={FaCheckCircle} boxSize={2.5} /><Text>Uploaded</Text></HStack>
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="gray.600" mb={3} lineHeight="1.5">{description}</Text>

          {uploaded && (
            <>
              <Text fontSize="xs" color="gray.500" mb={1} fontFamily="mono" noOfLines={1}>{value.filename}</Text>
              <FilePreview path={value.path} filename={value.filename} />
            </>
          )}

          <FormControl mt={3}>
            <HStack spacing={2}>
              <Button
                as="label"
                size="sm"
                colorScheme={uploaded ? 'gray' : 'blue'}
                variant={uploaded ? 'outline' : 'solid'}
                cursor="pointer"
                leftIcon={busy ? <Spinner size="xs" /> : <Icon as={FaUpload} />}
                isDisabled={busy || deleting}
              >
                {busy ? 'Uploading…' : uploaded ? 'Replace' : 'Upload file'}
                <Input type="file" accept={ACCEPT} display="none" onChange={onFile} />
              </Button>
              {uploaded && (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={deleting ? <Spinner size="xs" /> : <Icon as={FaTrash} />}
                  onClick={onDeleteClick}
                  isDisabled={busy || deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </Button>
              )}
            </HStack>
            <Text fontSize="2xs" color="gray.400" mt={2}>PDF, JPG, PNG, or WebP. Max 10 MB.</Text>
          </FormControl>

          {err && (
            <Alert status="error" fontSize="xs" mt={2} borderRadius="md">
              <AlertIcon />{err}
            </Alert>
          )}
        </Box>
      </HStack>
    </Box>
  );
}

export default function CredentialsUpload({ phlebId, value, onChange }) {
  const license = value.drivers_license;
  const cert = value.phlebotomy_cert;

  return (
    <VStack align="stretch" spacing={5}>
      <Box>
        <Heading size="sm">Upload your credentials</Heading>
        <Text fontSize="xs" color="gray.500" mt={1}>
          We need a copy of your driver's license and phlebotomy certification on file before
          your first draw. Documents are stored securely and only visible to our compliance team.
        </Text>
      </Box>

      <CredentialCard
        label="Driver's license"
        description="A clear photo or scan of the front of your current state-issued driver's license. Used to verify identity."
        icon={FaIdCard}
        kind="drivers_license"
        phlebId={phlebId}
        value={license}
        onUpload={(r) => onChange({ ...value, drivers_license: r })}
        onDelete={() => onChange({ ...value, drivers_license: null })}
      />

      <CredentialCard
        label="Phlebotomy certification"
        description="Your CPT, RPT, or state phlebotomy certification (PDF or image). If you have multiple credentials, upload the most current one."
        icon={FaCertificate}
        kind="phlebotomy_cert"
        phlebId={phlebId}
        value={cert}
        onUpload={(r) => onChange({ ...value, phlebotomy_cert: r })}
        onDelete={() => onChange({ ...value, phlebotomy_cert: null })}
      />
    </VStack>
  );
}

export function isCredentialsComplete(v) {
  return !!(v?.drivers_license?.path && v?.phlebotomy_cert?.path);
}
